import { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ChartColumn,
  ChevronDown,
  CircleCheckBig,
  CirclePlus,
  FileText,
  FolderOpen,
  LayoutGrid,
  LogOut,
  Network,
  Package,
  Factory,
  Shield,
  Sparkles,
  UserCog,
  Workflow,
} from 'lucide-react';
import { AppIcon } from '../LucideIcon';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { TenantLogo } from '@ravenappdev/raven-ui';
import { useAuth } from '../../contexts/AuthContext';
import SettingsDialog from '../SettingsDialog';
import { ALL_PLANTS_LABEL, routeIsActive, tenantLabel } from './railState';
import usePlantScope from './usePlantScope';
import useWorkspaceEnabled from './useWorkspaceEnabled';
import { plantApi } from '../../api';
import { clearPlantScope, plantFamilyNames, rememberPlantScope } from '../../lib/plantScope';
import type { PlantSummary } from '../../types';
import './product-shell.css';

type NavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
  visible?: boolean;
};

/*
 * MUI ListItemButton defaults to flex-grow: 1 (for row layouts inside ListItem).
 * Our nav List is a column flex container with the sidebar's free height, so
 * without flexGrow: 0 each row stretches to share that space instead of the
 * design system's compact 40px row.
 */
const itemSx = { borderRadius: 2, p: 1, minHeight: 40, gap: 1, flexGrow: 0, flexShrink: 0 } as const;
const collapsedItemSx = {
  ...itemSx,
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 0.25,
  /* 6px: the row's inset, matched on both sides. The rail is wide enough for
     the caption without borrowing from it. */
  px: 0.75,
  py: 0.75,
  minHeight: 52,
} as const;
const iconSx = { minWidth: 0 } as const;

function NavListItem({ item, active, collapsed, onNavigate }: {
  item: NavItem;
  active: boolean;
  collapsed?: boolean;
  onNavigate: (path: string) => void;
}) {
  const button = (
    <ListItemButton
      className={[
        'pnid-rail-item',
        collapsed ? 'pnid-rail-item--stacked' : '',
        active ? 'pnid-rail-item--active' : '',
      ].filter(Boolean).join(' ')}
      sx={collapsed ? collapsedItemSx : itemSx}
      onClick={() => onNavigate(item.path)}
      aria-current={active ? 'page' : undefined}
    >
      <ListItemIcon sx={iconSx}>{item.icon}</ListItemIcon>
      {collapsed ? (
        /* The glyph alone is a guess; the label under it names the destination
           without costing the rail its narrow width. */
        <span className="pnid-rail-item-caption">{item.label}</span>
      ) : (
        <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
      )}
    </ListItemButton>
  );
  /* The caption truncates on the longest labels, so the tooltip still carries
     the full text. */
  if (!collapsed) return button;
  return <Tooltip title={item.label} placement="right">{button}</Tooltip>;
}

export default function PnidNavigationRail({ onNavigated, collapsed = false }: {
  onNavigated?: () => void;
  /** Owned by PnidWorkspaceShell — the toggle lives on the frame bar. */
  collapsed?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const canManagePackages = Boolean(user?.permissions?.can_manage_packages);
  /* Matches PlantAssetsRoute, which opens the page to any account with a
     document scope — vendors and editors included, not just package managers.
     Gating this rail row on can_manage_packages hid it from users the route
     admits, so the URL was their only way in. The divider and the package group
     below it stay on can_manage_packages. */
  const canViewPlantAssets = Boolean(user?.user_group?.trim());
  const canViewDashboard = user?.username === 'raven_admin'
    || Boolean(user?.user_group?.endsWith('-reviewer'));
  const canAdmin = Boolean(user?.permissions?.can_admin);

  const settingsNavigation = useMemo<NavItem[]>(() => [
    { label: 'Isolation Planning', path: '/isolation-planning', icon: <AppIcon icon={Network} /> },
    { label: 'Analytics', path: '/dashboard', icon: <AppIcon icon={ChartColumn} />, visible: canViewDashboard },
    { label: 'Data Fields', path: '/admin', icon: <AppIcon icon={LayoutGrid} /> },
    { label: 'Policies', path: '/policy-admin', icon: <AppIcon icon={Shield} /> },
    { label: 'Administration', path: '/admin_dashboard', icon: <AppIcon icon={UserCog} />, visible: canAdmin },
  ], [canAdmin, canViewDashboard]);

  /* The group shows whenever it holds something. It used to be gated on
     `canAdmin || canViewDashboard`, which was stricter than the routes it leads
     to: Isolation Planning, Data Fields and Policies all mount behind a plain
     PrivateRoute, so a plain SME could open them by URL but had no link. Each
     item still carries its own `visible` gate. */
  const visibleSettings = settingsNavigation.filter((item) => item.visible !== false);


  const goTo = (path: string) => {
    navigate(path);
    onNavigated?.();
  };

  /* The avatar is the account menu. Settings used to be reachable only from
     the old picker's button row, so on every other page it did not exist; that
     page is gone and this is the only way in. The dialog writes the same
     `pnid_user_name` key App.tsx reads, so both surfaces agree. */
  const [accountMenu, setAccountMenu] = useState<HTMLElement | null>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [openSettingsAfterMenu, setOpenSettingsAfterMenu] = useState(false);
  /* MUI's Menu leaves focus on <body> when it closes, so the avatar is the
     only thing that can put a keyboard user back where they started. */
  const accountAnchorRef = useRef<HTMLElement | null>(null);
  const [displayName, setDisplayName] = useState(() => {
    try {
      return localStorage.getItem('pnid_user_name') || '';
    } catch {
      return '';
    }
  });
  const effectiveUserName = (user?.username || displayName || '').trim();
  const saveUserName = (next: string) => {
    try {
      localStorage.setItem('pnid_user_name', next);
    } catch {
      /* Session-only when storage is blocked. */
    }
    setDisplayName(next);
  };

  const tenant = tenantLabel(user?.user_group);

  /* The rail's identity is the current plant. Plants are fetched on FIRST OPEN,
     never on mount: mounting a plants query here would fire GET /plants on every
     shell route and warm the cache before the pages that actually need it. */
  const plantScope = usePlantScope();
  const workspaceEnabled = useWorkspaceEnabled();

  /* P&ID and PFD are the same drawings route split by a `type` query the library
     reads. The diagram-type toggle used to live on that page; the rail owns it
     now, so these two rows are the switch. No `type` (or anything but `pfd`)
     means P&ID — the default the library falls back to. */
  const drawingsActive = routeIsActive(location.pathname, '/drawings');
  const drawingType = new URLSearchParams(location.search).get('type');
  const pidActive = drawingsActive && drawingType !== 'pfd';
  const pfdActive = drawingsActive && drawingType === 'pfd';

  const [plantMenu, setPlantMenu] = useState<HTMLElement | null>(null);
  const [plants, setPlants] = useState<PlantSummary[] | null>(null);
  const [plantsError, setPlantsError] = useState(false);
  const plantAnchorRef = useRef<HTMLElement | null>(null);

  /* The menu names plants, it does not count them, so the only read it needs is
     the plant list itself — fetched once, on first open. The all-documents fetch
     that used to back an "All Plants" total is gone with that count; it moved a
     732 KB payload onto the menu's open path for one number. */
  const openPlantMenu = (event: React.MouseEvent<HTMLElement>) => {
    plantAnchorRef.current = event.currentTarget;
    setPlantMenu(event.currentTarget);
    if (plants || plantsError) return;
    plantApi.listPlants()
      .then(setPlants)
      .catch(() => setPlantsError(true));
  };

  const choosePlant = (plant: PlantSummary | null) => {
    setPlantMenu(null);
    if (plant) {
      rememberPlantScope(plant.domain_id, plant.plant, plantFamilyNames(plant, plants || []));
    } else {
      clearPlantScope();
    }
    /* The switcher is the app's "current plant", but only the drawings library
       reads that scope for its content. Choosing a plant from any other page
       (Plant Assets, Work, a drawing) therefore moved only the rail's identity
       tile and looked like it did nothing. Landing on Home makes every choice
       open that plant's drawings; on Home it is a no-op and the scope event
       filters the table in place. */
    navigate('/drawings');
    onNavigated?.();
    plantAnchorRef.current?.focus();
  };

  /* Unscoped reads "All Plants" — the same words the menu offers — not the
     tenant. The tenant is a different fact, and it already has a home in the
     account block at the foot of the rail. */
  /* One flat, alphabetical list of every plant. The menu used to group leaves
     under their `parent` heading (Train-1/Train-2 under Ammonia, …), but the
     parent rows were non-clickable labels that only duplicated a scope you
     already reach through the leaves — and one parent name doubled as a real
     selectable plant, so "Ammonia" appeared twice. Dropping the headings loses
     no reachability: every plant is still its own row. `choosePlant` still
     expands a leaf's family from the full `plants` list, independent of how the
     menu is laid out. */
  const sortedPlants = useMemo(() => (
    plants ? plants.slice().sort((a, b) => a.plant.localeCompare(b.plant)) : []
  ), [plants]);

  const identityLabel = plantScope.name || ALL_PLANTS_LABEL;
  const identityInitials = plantScope.name
    ? (plantScope.name.replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?')
    : null;

  return (
    <Box className={collapsed ? 'pnid-sidebar pnid-sidebar--collapsed' : 'pnid-sidebar'} component="div">
      {collapsed ? (
        /* The tenant identity, with the two receding cards behind it. The
           trail uses negative z-index, so this element MUST form its own
           stacking context — without `isolation: isolate` (in the CSS) the
           cards fall behind the shell's painted gradient and vanish. */
        <div className="pnid-rail-identity">
          <button
            type="button"
            className="pnid-rail-identity__button"
            aria-haspopup="menu"
            aria-expanded={Boolean(plantMenu)}
            aria-label={plantScope.name ? `Plant: ${plantScope.name}. Switch plant` : 'Select a plant'}
            onClick={openPlantMenu}
          >
            <span className="pnid-rail-tile pnid-rail-tile--identity">
              {identityInitials
                ? <span>{identityInitials}</span>
                : <AppIcon icon={LayoutGrid} size={20} />}
            </span>
            <span className="pnid-rail-identity__label">{identityLabel}</span>
          </button>
          <span className="pnid-rail-rule" aria-hidden />
        </div>
      ) : (
        <Box
          component="button"
          type="button"
          className="pnid-sidebar-logo pnid-sidebar-logo--button"
          aria-haspopup="menu"
          aria-expanded={Boolean(plantMenu)}
          aria-label={plantScope.name ? `Plant: ${plantScope.name}. Switch plant` : 'Select a plant'}
          onClick={openPlantMenu}
        >
          <TenantLogo tenantName={identityLabel} size="md" onBackground="dark" />
          <Typography component="span" variant="subtitle2" fontWeight={600} sx={{ flex: 1, textAlign: 'left' }} noWrap>
            {identityLabel}
          </Typography>
          <AppIcon icon={ChevronDown} size={16} />
        </Box>
      )}

      {/* px: 0 — MUI's List keeps 8px of side padding even with disablePadding,
          and in an 84px rail that truncated "Add Drawings" to "Add Drawi…". */}
      <List component="nav" aria-label="Primary" disablePadding sx={{ flex: 1, pt: 1, px: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* The library's diagram-type filter, lifted into the rail. P&ID leads
            because it is the bulk of every package and the app's default view. */}
        <NavListItem
          item={{ label: 'P&ID', path: '/drawings', icon: <AppIcon icon={FileText} /> }}
          active={pidActive}
          collapsed={collapsed}
          onNavigate={goTo}
        />
        <NavListItem
          item={{ label: 'PFD', path: '/drawings?type=pfd', icon: <AppIcon icon={Workflow} /> }}
          active={pfdActive}
          collapsed={collapsed}
          onNavigate={goTo}
        />
        {canViewPlantAssets && (
          <NavListItem
            item={{ label: 'Assets', path: '/plant-hierarchy', icon: <AppIcon icon={Package} /> }}
            active={routeIsActive(location.pathname, '/plant-hierarchy')}
            collapsed={collapsed}
            onNavigate={goTo}
          />
        )}
        {canViewPlantAssets && (
          <NavListItem
            item={{ label: 'Legend sheets', path: '/legend-sheets', icon: <AppIcon icon={BookOpen} /> }}
            active={routeIsActive(location.pathname, '/legend-sheets')}
            collapsed={collapsed}
            onNavigate={goTo}
          />
        )}

        {/* Divides what you look at (the drawings and their register) from what
            you do to a package. The whole lower group is package management, so
            it — and the rule — appear only for users who can manage packages. */}
        {canManagePackages && (
          <Divider className="pnid-rail-divider" sx={{ mx: 1, my: 0.5 }} />
        )}
        {canManagePackages && (
          <NavListItem
            item={{ label: 'Add Drawings', path: '/add', icon: <AppIcon icon={CirclePlus} /> }}
            active={routeIsActive(location.pathname, '/add')}
            collapsed={collapsed}
            onNavigate={goTo}
          />
        )}
        {canManagePackages && (
          <NavListItem
            item={{ label: 'File Manager', path: '/file-manager', icon: <AppIcon icon={FolderOpen} /> }}
            active={routeIsActive(location.pathname, '/file-manager')}
            collapsed={collapsed}
            onNavigate={goTo}
          />
        )}
        {canManagePackages && (
          <NavListItem
            item={{ label: 'Work', path: '/work', icon: <AppIcon icon={CircleCheckBig} /> }}
            active={location.pathname !== '/add' && routeIsActive(location.pathname, '/work')}
            collapsed={collapsed}
            onNavigate={goTo}
          />
        )}
        {/* Same gate as PackageRoute. Reachable only from the library's Ask Raven
            button before this, so it never appeared in navigation. Hidden
            rather than disabled while the workspace flag is off: a permanently
            dead rail row is just clutter, and the library's CTA is the place
            that explains why the feature is unavailable. */}
        {canManagePackages && workspaceEnabled !== false && (
          <NavListItem
            item={{ label: 'Ask Raven', path: '/workspace', icon: <AppIcon icon={Sparkles} /> }}
            active={routeIsActive(location.pathname, '/workspace')}
            collapsed={collapsed}
            onNavigate={goTo}
          />
        )}

      </List>

      {/* mt: auto — the removed Sign Out row used to pin the foot down. */}
      <Divider className="pnid-rail-divider" sx={{ mt: 'auto', mx: 1, my: 0.5 }} />

      <Box
        component="button"
        type="button"
        className="pnid-sidebar-account pnid-sidebar-account--button"
        aria-haspopup="menu"
        aria-expanded={Boolean(accountMenu)}
        aria-label={`Account menu for ${effectiveUserName || 'this account'}`}
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          accountAnchorRef.current = event.currentTarget;
          setAccountMenu(event.currentTarget);
        }}
        sx={collapsed ? { justifyContent: 'center' } : undefined}
      >
        <Avatar className="pnid-rail-avatar" sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
          {(user?.username || '?').slice(0, 2).toUpperCase()}
        </Avatar>
        {!collapsed && (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap>{user?.username || 'Signed in'}</Typography>
            <Typography variant="caption" noWrap component="div" className="pnid-rail-account-sub">
              {user?.role || 'SME'} · {tenant} account
            </Typography>
          </Box>
        )}
      </Box>

      {!collapsed && (
        <>
          <Divider className="pnid-rail-divider" sx={{ mx: 1, my: 0.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, p: '8px 4px' }}>
            <Typography variant="caption" className="pnid-rail-foot-by">by</Typography>
            <Typography variant="caption" fontWeight={700} className="pnid-rail-foot-mark">raven</Typography>
          </Box>
        </>
      )}

      <Menu
        anchorEl={plantMenu}
        open={Boolean(plantMenu)}
        onClose={() => setPlantMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { className: 'pnid-plant-menu' } }}
      >
        <p className="pnid-plant-menu__head">Switch Plant</p>
        <MenuItem
          className="pnid-account-menu-item"
          selected={!plantScope.domainId}
          onClick={() => choosePlant(null)}
        >
          <ListItemIcon sx={iconSx}><AppIcon icon={LayoutGrid} size={20} /></ListItemIcon>
          <ListItemText primary={ALL_PLANTS_LABEL} primaryTypographyProps={{ variant: 'body1' }} />
        </MenuItem>
        {plants === null && !plantsError && (
          <p className="pnid-plant-menu__note">Loading plants…</p>
        )}
        {plantsError && (
          <p className="pnid-plant-menu__note">Plants could not be loaded.</p>
        )}
        {plants?.length === 0 && (
          <p className="pnid-plant-menu__note">No plants in this account yet.</p>
        )}
        {sortedPlants.map((plant) => (
          <MenuItem
            key={plant.domain_id}
            className="pnid-account-menu-item"
            selected={plant.domain_id === plantScope.domainId}
            onClick={() => choosePlant(plant)}
          >
            <ListItemIcon sx={iconSx}><AppIcon icon={Factory} size={20} /></ListItemIcon>
            <ListItemText primary={plant.plant} primaryTypographyProps={{ variant: 'body1' }} />
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={accountMenu}
        open={Boolean(accountMenu)}
        onClose={() => setAccountMenu(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        /* The dialog waits for the menu to finish closing. Opening it inline
           would capture the about-to-unmount MenuItem as the focus opener, so
           closing the dialog would drop focus on <body>. After the transition
           MUI has restored focus to the avatar, which is the right anchor. */
        TransitionProps={{
          onExited: () => {
            if (!openSettingsAfterMenu) return;
            setOpenSettingsAfterMenu(false);
            setSettingsDialogOpen(true);
          },
        }}
      >
        {visibleSettings.map((item) => (
          <MenuItem
            key={item.path}
            className="pnid-account-menu-item"
            selected={routeIsActive(location.pathname, item.path)}
            onClick={() => {
              setAccountMenu(null);
              goTo(item.path);
            }}
          >
            <ListItemIcon sx={iconSx}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body1' }} />
          </MenuItem>
        ))}
        {visibleSettings.length > 0 && <Divider className="pnid-account-menu-rule" />}
        <MenuItem
          className="pnid-account-menu-item"
          onClick={() => {
            setAccountMenu(null);
            setOpenSettingsAfterMenu(true);
          }}
        >
          <ListItemIcon sx={iconSx}><AppIcon icon={UserCog} size={20} /></ListItemIcon>
          <ListItemText primary="My Profile" primaryTypographyProps={{ variant: 'body1' }} />
        </MenuItem>
        <MenuItem
          className="pnid-account-menu-item"
          onClick={() => {
            setAccountMenu(null);
            logout();
          }}
        >
          <ListItemIcon sx={iconSx}><AppIcon icon={LogOut} size={20} /></ListItemIcon>
          <ListItemText primary="Sign Out" primaryTypographyProps={{ variant: 'body1' }} />
        </MenuItem>
      </Menu>

      <SettingsDialog
        isOpen={settingsDialogOpen}
        currentUser={effectiveUserName}
        onClose={() => {
          setSettingsDialogOpen(false);
          accountAnchorRef.current?.focus();
        }}
        onSaveUser={saveUserName}
      />
    </Box>
  );
}
